import { Button, Input, notification, Select, Typography } from 'antd';
import { deleteDestinationContent } from 'api/destinations.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import { useFormDirtyState } from 'hooks/use-form-dirty-state';
import React, { useCallback, useState } from 'react';
import { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { SortableList } from '../GuideEditor/SortableList';
import { useSort } from '../GuideEditor/SortableList/use-sort';
import type {
    Destination,
    UpdateDestinationContentFieldChange,
} from './DestinationEditorContext';
import { useDestinationEditor } from './DestinationEditorContext';
import { useSyncedDestinationContents } from './use-synced-destination-contents';

import FormField from 'components/FormField';
import { GUIDE_GOOD_TO_KNOW_DESCRIPTION_MAX_LENGTH } from 'constants/fields-limits';
import {
    UpdateTranslationsValue,
    useDestinationContentTranslation,
} from 'hooks/use-destination-content-translation';
import type { DestinationContentFieldErrors } from 'hooks/use-destination-content-validation';
import { useDestinationContentValidation } from 'hooks/use-destination-content-validation';
import styles from './DestinationEditorGoodToKnow.module.css';

type DestinationEditorGoodToKnowProps = {
    onClickDiscard: () => void;
};

const mapGoodToKnowTypes = (item: DestinationContentSchema) => {
    if (!item.title) return item;

    for (const typeKey of Object.keys(goodToKnowTypes)) {
        // Look for the exact **keyword** format (including asterisks) in the title
        if (item.title.toLowerCase().includes(typeKey.toLowerCase())) {
            return {
                ...item,
                title: typeKey,
            };
        }
    }

    // No match found, return item with blank title
    return {
        ...item,
        title: null,
    };
};

export function DestinationEditorGoodToKnow({
    onClickDiscard,
}: DestinationEditorGoodToKnowProps) {
    const {
        destination,
        saveBulkContent,
        updateDestination,
    } = useDestinationEditor();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [
        goodToKnowItems,
        setGoodToKnowItems,
    ] = useSyncedDestinationContents(
        DestinationContentTypes.GOOD_TO_KNOW,
        (items) => items.map(mapGoodToKnowTypes),
    );
    const { fieldErrors, validateFields } = useDestinationContentValidation(
        goodToKnowItems,
        ['title', 'body'],
        {
            hasUniqueTitle: true,
        },
    );

    const { onMoveUp, onMoveDown } = useSort(goodToKnowItems);

    const { isDirty, updateOriginalValues } = useFormDirtyState({
        values: goodToKnowItems,
    });

    const handleUpdateFields = (
        changes: UpdateDestinationContentFieldChange[],
    ) => {
        let updatedItems = goodToKnowItems;
        for (const change of changes) {
            updatedItems = updatedItems.map((item) =>
                item.id === change.id
                    ? { ...item, [change.fieldName]: change.newValue }
                    : item,
            );
        }
        setGoodToKnowItems(updatedItems);

        if (isSubmitted) {
            validateFields(updatedItems);
        }
    };

    const handleUpdateField = (change: UpdateDestinationContentFieldChange) => {
        handleUpdateFields([change]);
    };

    const handleUpdateTranslationField = ({
        contentId,
        translations,
    }: UpdateTranslationsValue) => {
        setGoodToKnowItems((prev) =>
            prev.map((item) =>
                item.id === contentId
                    ? {
                          ...item,
                          translations,
                      }
                    : item,
            ),
        );
    };

    const handleAdd = () => {
        if (!destination.id) {
            return;
        }

        const newItem = getEmptyGoodToKnowItem(
            destination.id,
            goodToKnowItems.length,
        );

        setGoodToKnowItems(goodToKnowItems.concat([newItem]));
    };

    const handleDelete = async ({ id }: DestinationContentSchema) => {
        try {
            setIsDeleting(true);

            if (id > 0) {
                await deleteDestinationContent(id);
            }

            setGoodToKnowItems((prev) =>
                prev
                    .filter((item) => item.id !== id)
                    .map((item, index) => ({
                        ...item,
                        order: index,
                    })),
            );
        } catch (error) {
            notification.error({
                message: 'Error deleting good to know item',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMoveUp = (item: DestinationContentSchema) => {
        const sortedItems = onMoveUp(item);
        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>(
                    (item) => ({
                        id: item.id,
                        fieldName: 'order',
                        newValue: item.newOrder,
                    }),
                ),
            );
        }
    };

    const handleMoveDown = (item: DestinationContentSchema) => {
        const sortedItems = onMoveDown(item);
        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>(
                    (item) => ({
                        id: item.id,
                        fieldName: 'order',
                        newValue: item.newOrder,
                    }),
                ),
            );
        }
    };

    const handleSave = async () => {
        setIsSubmitted(true);

        if (!validateFields(goodToKnowItems)) {
            notification.error({
                message: 'Please fix the errors before saving',
            });
            return;
        }

        setIsSaving(true);

        try {
            if (!destination.id) {
                throw new Error('Destination ID is required');
            }

            // Bulk save all good to know items at once
            const savedContents = await saveBulkContent({
                destinationId: destination.id,
                contents: goodToKnowItems.map((item) => {
                    const contentId = item.id > 0 ? item.id : undefined;
                    return {
                        ...item,
                        id: contentId,
                        translations: item.translations,
                    };
                }),
            });

            // Filter to only get good to know items (API might return other content types)
            const savedGoodToKnowItems = savedContents.filter(
                (content) =>
                    content.content_type ===
                    DestinationContentTypes.GOOD_TO_KNOW,
            );

            // Update local state with saved items
            setGoodToKnowItems(savedGoodToKnowItems);

            // Update the destination - remove old good to know items and add saved ones
            const nonGoodToKnowContents = (destination.contents ?? []).filter(
                (content) =>
                    content.content_type !==
                    DestinationContentTypes.GOOD_TO_KNOW,
            );

            updateDestination({
                ...destination,
                contents: [...nonGoodToKnowContents, ...savedGoodToKnowItems],
            });

            notification.success({
                message: 'Good to know items saved successfully',
                key: 'good-to-know-items-saved',
            });

            // Update original values after successful save
            updateOriginalValues();
        } catch (error) {
            notification.error({
                message: 'Error saving good to know items',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handlePreloadDefaultItems = () => {
        setGoodToKnowItems((prev) =>
            prev.concat(generateDefaultItems(destination)),
        );
    };

    if (!destination) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <SortableList
                title="Good to know"
                data={goodToKnowItems}
                onAdd={handleAdd}
                onDelete={handleDelete}
                onMoveDown={handleMoveDown}
                onMoveUp={handleMoveUp}
                isLoading={isSaving || isDeleting}
            >
                {({ data, index }) =>
                    !data ? (
                        <SortableList.EmptyState
                            message="No good to know items yet"
                            action={
                                <SortableList.AddButton
                                    onClick={handlePreloadDefaultItems}
                                    disabled={isSaving}
                                >
                                    Preload default items
                                </SortableList.AddButton>
                            }
                        />
                    ) : (
                        <DestinationEditorGoodToKnowItem
                            key={data.id}
                            index={index}
                            goodToKnowItem={data}
                            onChangeField={handleUpdateField}
                            onChangeTranslations={handleUpdateTranslationField}
                            isSubmitted={isSubmitted}
                            errors={fieldErrors[data.id] ?? {}}
                        />
                    )
                }
            </SortableList>
            <div className="full-page-tabs-footer">
                <div className="max-content-width">
                    <div className="grow-full-flex"></div>
                    <Button size="large" onClick={onClickDiscard}>
                        Cancel
                    </Button>
                    <Button
                        size="large"
                        type="primary"
                        loading={isSaving}
                        disabled={isDeleting || !isDirty}
                        onClick={handleSave}
                    >
                        Save changes
                    </Button>
                </div>
            </div>
        </>
    );
}

type DestinationEditorGoodToKnowItemProps = {
    index: number;
    goodToKnowItem: DestinationContentSchema;
    onChangeField: (change: UpdateDestinationContentFieldChange) => void;
    onChangeTranslations: (value: UpdateTranslationsValue) => void;
    isSubmitted?: boolean;
    errors?: DestinationContentFieldErrors;
};

function DestinationEditorGoodToKnowItem({
    index,
    goodToKnowItem,
    onChangeField,
    onChangeTranslations,
    isSubmitted = false,
    errors = {},
}: DestinationEditorGoodToKnowItemProps) {
    const { locale } = useDestinationEditor();

    const {
        translatedContent: goodToKnowItemValue,
        isTranslation,
        handleUpdateTranslationField,
    } = useDestinationContentTranslation({
        content: goodToKnowItem,
        locale,
    });

    const handleChangeField = useCallback(
        <T extends keyof DestinationContentSchema>(
            fieldName: T,
            newValue: DestinationContentSchema[T],
        ) => {
            if (fieldName === 'body' && isTranslation) {
                onChangeTranslations({
                    contentId: goodToKnowItem.id,
                    translations: handleUpdateTranslationField({
                        [fieldName]: newValue,
                    }),
                });
            } else {
                onChangeField({
                    id: goodToKnowItem.id,
                    fieldName: fieldName,
                    newValue: newValue,
                });
            }
        },
        [
            goodToKnowItem.id,
            handleUpdateTranslationField,
            isTranslation,
            onChangeField,
            onChangeTranslations,
        ],
    );

    return (
        <div className={styles.item}>
            <Typography.Title level={5}>{`Item ${index + 1}`}</Typography.Title>
            <div className={styles.itemContent}>
                <div className={styles.form}>
                    <FormField
                        label="Type"
                        labelWidth={100}
                        className={styles.noMargin}
                        error={isSubmitted ? errors.title : undefined}
                    >
                        <Select
                            size="large"
                            defaultValue={null}
                            value={goodToKnowItem.title}
                            status={isSubmitted && errors.title ? 'error' : ''}
                            onChange={(value) =>
                                handleChangeField('title', value)
                            }
                            className={styles.typeSelect}
                            disabled={isTranslation}
                        >
                            {Object.entries(goodToKnowTypes).map(
                                ([title, label]) => (
                                    <Select.Option key={title} value={title}>
                                        {label}
                                    </Select.Option>
                                ),
                            )}
                        </Select>
                    </FormField>

                    <FormField
                        label="Description"
                        length={goodToKnowItemValue.body?.length ?? 0}
                        maxLength={GUIDE_GOOD_TO_KNOW_DESCRIPTION_MAX_LENGTH}
                        labelWidth={100}
                        error={isSubmitted ? errors.body : undefined}
                        className={styles.noMargin}
                    >
                        <Input.TextArea
                            size="large"
                            value={goodToKnowItemValue.body ?? ''}
                            autoSize={{
                                minRows: 1,
                                maxRows: 4,
                            }}
                            status={isSubmitted && errors.body ? 'error' : ''}
                            onChange={(e) =>
                                handleChangeField('body', e.target.value)
                            }
                        />
                    </FormField>
                </div>
            </div>
        </div>
    );
}

function getEmptyGoodToKnowItem(
    destinationId: number,
    order: number,
): DestinationContentSchema {
    return {
        // Use negative id to ensure new items are added at the end, this is needed because the List component uses id as the key
        id: -order,
        destination_id: destinationId,
        content_type: DestinationContentTypes.GOOD_TO_KNOW,
        order,
        body: '',
        source: DestinationContentSources.MANUAL,
        title: '',
        cms_media: null,
        translations: [],
    };
}

const goodToKnowTypes = {
    '**vibe**': "What's the vibe like?",
    '**stay**': 'Where should I stay?',
    '**insider tip**': 'Best insider tip?',
    '**best time to visit**': 'Best time to visit?',
    '**get to**': 'How to get there?',
    '**need to know**': 'What else to know?',
};

function generateDefaultItems(
    destination: Destination,
): DestinationContentSchema[] {
    return destination?.id
        ? Object.keys(goodToKnowTypes).map((title, index) => ({
              id: -index,
              destination_id: destination.id,
              content_type: DestinationContentTypes.GOOD_TO_KNOW,
              order: index,
              body: '',
              source: DestinationContentSources.MANUAL,
              title,
              cms_media: null,
              translations: [],
          }))
        : [];
}
